
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Plus } from "lucide-react";
import { BookingFormData } from "../ParcelBookingForm";
import ParcelForm from "../parcel-form/ParcelForm";
import CollectionDateTimeSelector from "../parcel-form/CollectionDateTimeSelector";
import TestTimeControls from "../parcel-form/TestTimeControls";
import DeliveryPreferenceSelector from "../parcel-form/DeliveryPreferenceSelector";
import InsuranceInformation from "../parcel-form/InsuranceInformation";
import { useParcelValidation } from "@/hooks/useParcelValidation";
import { useUserRoles } from "@/hooks/useUserRoles";
import { useEffect, useState, useMemo } from "react";

interface ParcelStepProps {
  formData: BookingFormData;
  setFormData: (data: BookingFormData) => void;
  onValidationChange: (isValid: boolean) => void;
}

const ParcelStep = ({ formData, setFormData, onValidationChange }: ParcelStepProps) => {
  const { errors, isValid } = useParcelValidation(formData.parcels, formData.collectionDateTime);
  const { roles } = useUserRoles();
  const isSuperAdmin = roles.platformRole === 'super_admin';
  
  // Test time controls state
  const [currentTestTime, setCurrentTestTime] = useState<Date>(new Date());
  const [showTestControls, setShowTestControls] = useState<boolean>(false);

  // Check if any parcel has insurance enabled
  const hasInsurance = useMemo(() => {
    return formData.parcels.some(parcel => parcel.insuranceEnabled);
  }, [formData.parcels]);

  useEffect(() => {
    onValidationChange(isValid);
  }, [isValid, onValidationChange]);

  const addParcel = () => {
    const newParcel = {
      type: 'documents',
      description: '',
      weight: '',
      dimensions: {
        length: '',
        width: '',
        height: '',
      },
      value: '',
      quantity: 1,
      otherTypeSpecification: '',
      insuranceEnabled: false,
    };
    
    setFormData({
      ...formData,
      parcels: [...formData.parcels, newParcel]
    });
  };

  const removeParcel = (index: number) => {
    const updatedParcels = [...formData.parcels];
    updatedParcels.splice(index, 1);
    setFormData({
      ...formData,
      parcels: updatedParcels
    });
  };

  const updateParcel = (index: number, field: string, value: any) => {
    const updatedParcels = [...formData.parcels];
    
    if (field.includes('.')) {
      // Handle nested fields like dimensions
      const [parent, child] = field.split('.');
      updatedParcels[index][parent] = {
        ...updatedParcels[index][parent],
        [child]: value
      };
    } else {
      updatedParcels[index][field] = value;
    }

    setFormData({
      ...formData,
      parcels: updatedParcels
    });
  };

  const toggleTestControls = () => {
    setShowTestControls(!showTestControls);
  };

  const handleTestTimeChange = (newTime: Date) => {
    setCurrentTestTime(newTime);
  };

  return (
    <div className="space-y-6">
      {/* Test Time Controls - Only for superadmins */}
      {isSuperAdmin && (
        <TestTimeControls
          showTestControls={showTestControls}
          onToggle={toggleTestControls}
          currentTime={currentTestTime}
          onTimeChange={handleTestTimeChange}
        />
      )}

      {/* Collection Date and Time */}
      <CollectionDateTimeSelector 
        collectionDateTime={formData.collectionDateTime}
        onUpdate={(dateTime) => setFormData({ ...formData, collectionDateTime: dateTime })}
        currentTestTime={currentTestTime}
      />

      {/* Delivery Preference */}
      <DeliveryPreferenceSelector
        deliveryPreference={formData.deliveryPreference}
        onUpdate={(preference) => setFormData({ ...formData, deliveryPreference: preference })}
      />

      {/* Instructions */}
      <div className="space-y-2">
        <Label htmlFor="instructions">Special Instructions</Label>
        <Textarea
          id="instructions"
          placeholder="Add any information that would assist the driver at collection/delivery points"
          value={formData.instructions}
          onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
        />
      </div>

      {/* Parcels */}
      <div className="space-y-4">
        {formData.parcels.map((parcel, index) => (
          <ParcelForm
            key={index}
            parcel={parcel}
            index={index}
            onUpdate={updateParcel}
            onRemove={removeParcel}
            canRemove={formData.parcels.length > 1}
            errors={errors.parcels[index]}
          />
        ))}
        <Button variant="outline" size="sm" className="w-full justify-center" onClick={addParcel}>
          <Plus className="h-4 w-4 mr-2" />
          Add Another Parcel
        </Button>
      </div>

      {/* Insurance Information */}
      <InsuranceInformation hasInsurance={hasInsurance} />
    </div>
  );
};

export default ParcelStep;
